/**
 * KanbanStore - MobX observable store for kanban board state.
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 * @implements US-057 - Client MobX Store + Subscription Wiring
 *
 * Two-section store:
 * - `server`: Written exclusively by subscription events and initial query.
 *   Contains tickets, parentColumns, validationStatuses, ticketsByColumn.
 * - `local`: Ephemeral UI state. Contains pendingMoves, loading, error.
 *   Never persisted.
 *
 * Display merge: effective column = local.pendingMoves[ticketId] || server.tickets[ticketId].column
 *
 * No SAM container, no acceptors, no NAPs on the client.
 */

import { makeAutoObservable, runInAction } from 'mobx';

// ============================================================================
// Types matching the server's GraphQL subscription payload (KanbanRepresentation)
// ============================================================================

/**
 * A ticket in the subscription payload.
 * Matches the KanbanTicket SDL type.
 * @implements FS-005.8.8 - GraphQL Subscription Protocol
 */
export interface KanbanTicketPayload {
  id: string;
  type: string;
  title: string;
  column: string;
  lifecycleState: string;
  parentId: string | null;
  children: Array<{ id: string | null; text: string }>;
  specReferences: string[];
  acceptanceCriteria: string[];
  description: string | null;
  context: string | null;
  validationFeedback: Array<{
    agentType: string;
    status: string;
    feedback?: string;
    timestamp: string;
  }>;
}

/**
 * Full kanban board representation from subscription/query.
 * Matches the KanbanRepresentation SDL type.
 * @implements FS-005.8.8 - GraphQL Subscription Protocol
 */
export interface KanbanRepresentationPayload {
  tickets: KanbanTicketPayload[];
  parentColumns: Array<{ ticketId: string; column: string }>;
  validationStatuses: Array<{ ticketId: string; status: string }>;
  ticketsByColumn: Array<{ column: string; tickets: KanbanTicketPayload[] }>;
}

/**
 * Server section: authoritative state from subscription/query.
 * Record-based for O(1) lookups.
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 */
export interface ServerState {
  tickets: Record<string, KanbanTicketPayload>;
  parentColumns: Record<string, string>;
  validationStatuses: Record<string, string>;
  ticketsByColumn: Record<string, KanbanTicketPayload[]>;
}

/**
 * Local section: ephemeral UI state, never persisted.
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 */
export interface LocalState {
  pendingMoves: Record<string, string>;
  loading: boolean;
  error: string | null;
}

// ============================================================================
// KanbanStore
// ============================================================================

/**
 * MobX observable store for kanban board state.
 * Separates authoritative server state from ephemeral local UI state.
 *
 * @implements FS-005.8.7 - Client Architecture (MobX Observable Store)
 * @implements US-057 - Client MobX Store + Subscription Wiring
 */
export class KanbanStore {
  server: ServerState = {
    tickets: {},
    parentColumns: {},
    validationStatuses: {},
    ticketsByColumn: {},
  };

  local: LocalState = {
    pendingMoves: {},
    loading: false,
    error: null,
  };

  constructor() {
    makeAutoObservable(this);
  }

  /**
   * Write server section from subscription/query payload.
   * Converts list-based payload to record-based server state.
   * @implements FS-005.8.7 - Server section is written exclusively by subscription events
   */
  updateFromServer(payload: KanbanRepresentationPayload): void {
    runInAction(() => {
      const tickets: Record<string, KanbanTicketPayload> = {};
      for (const t of payload.tickets) {
        tickets[t.id] = t;
      }

      const parentColumns: Record<string, string> = {};
      for (const p of payload.parentColumns) {
        parentColumns[p.ticketId] = p.column;
      }

      const validationStatuses: Record<string, string> = {};
      for (const v of payload.validationStatuses) {
        validationStatuses[v.ticketId] = v.status;
      }

      const ticketsByColumn: Record<string, KanbanTicketPayload[]> = {};
      for (const c of payload.ticketsByColumn) {
        ticketsByColumn[c.column] = c.tickets;
      }

      this.server = {
        tickets,
        parentColumns,
        validationStatuses,
        ticketsByColumn,
      };
    });
  }

  /**
   * Set a pending optimistic move for a ticket.
   * @implements FS-005.8.7 - Local section manages pendingMoves
   */
  setPendingMove(ticketId: string, toColumn: string): void {
    runInAction(() => {
      this.local.pendingMoves[ticketId] = toColumn;
    });
  }

  /**
   * Clear a pending move (called on mutation resolution, success or failure).
   * @implements FS-005.8.7 - Self-healing drift: pending move cleared on mutation resolution
   */
  clearPendingMove(ticketId: string): void {
    runInAction(() => {
      delete this.local.pendingMoves[ticketId];
    });
  }

  /**
   * Set loading state.
   */
  setLoading(loading: boolean): void {
    runInAction(() => {
      this.local.loading = loading;
    });
  }

  /**
   * Set error message.
   */
  setError(error: string | null): void {
    runInAction(() => {
      this.local.error = error;
    });
  }

  /**
   * Clear error (convenience wrapper).
   */
  clearError(): void {
    this.setError(null);
  }

  /**
   * Set a structured error message based on errorType classification.
   * Maps server errorType to user-friendly messages.
   * @implements BS-005.22 - Error Semantics
   * @implements US-072 - Pro Tier Frontend Fallback
   */
  setStructuredError(
    errorType: string | null | undefined,
    rejectionReason: string | null | undefined,
  ): void {
    runInAction(() => {
      if (errorType === 'REJECTION') {
        this.local.error = `Cannot move ticket: ${rejectionReason}`;
      } else if (errorType === 'INFRASTRUCTURE') {
        this.local.error = 'Server error — please try again';
      } else if (errorType === 'DISCONNECTION') {
        this.local.error =
          'Lost connection to server — please refresh the page';
      } else if (errorType === 'UNCHANGED') {
        this.local.error = 'No changes were made';
      } else if (rejectionReason) {
        this.local.error = rejectionReason;
      } else {
        this.local.error = 'An unexpected error occurred';
      }
    });
  }

  /**
   * Display merge: get effective column for a ticket.
   * Returns pending move column if one exists, otherwise server column.
   * Returns undefined if ticket is unknown in both local and server state.
   *
   * @implements FS-005.8.7 - Display merge logic
   */
  getEffectiveColumn(ticketId: string): string | undefined {
    const pendingColumn = this.local.pendingMoves[ticketId];
    if (pendingColumn) return pendingColumn;
    const ticket = this.server.tickets[ticketId];
    return ticket?.column;
  }
}
